// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Comment Dialog for I-9 documents
 *
 * Attaches a free-text reviewer note to a single document without changing its status
 * or moving it to another tab (unlike Mark bad data). Pre-fills with the document's
 * existing comment so it can be edited or cleared.
 */
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Textarea,
} from '@/components/ui'
import { MessageSquare } from 'lucide-react'

interface CommentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  docId: string | null
  initialComment: string | null
  isSaving: boolean
  onConfirm: (comment: string | null) => void
}

export const CommentDialog: FC<CommentDialogProps> = ({
  open,
  onOpenChange,
  docId,
  initialComment,
  isSaving,
  onConfirm,
}) => {
  const [comment, setComment] = useState('')

  // Re-seed the field whenever a different document's dialog opens.
  useEffect(() => {
    if (open) setComment(initialComment ?? '')
  }, [open, initialComment])

  const handleConfirm = () => {
    const trimmed = comment.trim()
    onConfirm(trimmed || null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Comment
          </DialogTitle>
          <DialogDescription>
            Attach a note to <span className="font-mono">{docId}</span>. This doesn&apos;t
            change the document&apos;s status — it stays in its current tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <Label htmlFor="documentComment">Comment</Label>
          <Textarea
            id="documentComment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. front page missing"
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isSaving || !docId}>
            <MessageSquare className="h-4 w-4 mr-1" />
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
